import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, Phone } from "lucide-react";
import BrickTitle from "@/components/landing/BrickTitle";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { PayPalDirectDeposit } from "@/components/landing/PayPalDirectDeposit";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Deposit() {
  const [config, setConfig] = useState(null);
  const [paid, setPaid] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/paypal/config`)
      .then((r) => setConfig(r.data))
      .catch(() => setLoadError(true));
  }, []);

  const amount = config?.amount;

  return (
    <section id="deposit" className="py-20 max-w-4xl mx-auto px-4 sm:px-6" data-testid="deposit-section">
      <div className="text-center mb-10">
        <span className="text-xs font-bold uppercase tracking-widest text-[#D4691E]">Secure Your Spot</span>
        <h2 data-testid="deposit-heading" className="text-3xl font-bold mt-2"><BrickTitle text={amount ? `Lock In Your Visit With a $${Number(amount)} Deposit` : "Lock In Your Visit"} /></h2>
        <p className="text-sm text-[#A89F94] mt-2 max-w-xl mx-auto">
          Priority scheduling for customers who book ahead. Your deposit is applied directly to your final invoice —
          and fully refunded if we can't get you on the schedule.
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-[#E6DCD3] p-5 sm:p-10 shadow-sm max-w-xl mx-auto text-center">
        {loadError ? (
          <div role="alert" data-testid="deposit-config-error" className="text-sm text-[#C7BFB6]">
            Payment details couldn't load. Please <a data-testid="deposit-error-call-link" className="text-white underline" href="tel:+12258927777">call Will</a> to arrange your deposit.
          </div>
        ) : !config ? (
          <p role="status" data-testid="deposit-config-loading" className="text-sm text-[#C7BFB6]">Loading payment details…</p>
        ) : config.payment_url ? (
          <PayPalDirectDeposit config={config} />
        ) : paid ? (
          <div data-testid="deposit-success-message">
            <div className="w-16 h-16 rounded-full bg-emerald-50 border-2 border-emerald-500 flex items-center justify-center mx-auto mb-4">
              <ShieldCheck className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="font-display text-2xl font-bold text-[#1E252B]">Deposit Received</h3>
            <p className="text-sm text-[#665E56] mt-3">
              You're locked in for priority scheduling. I'll call you to confirm your visit window — and your free
              10-Point Home Health Inspection bonus comes with it.
            </p>
            <a
              href="tel:+12258927777"
              data-testid="deposit-call-link"
              className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-lg bg-[#1E252B] hover:bg-[#D4691E] text-white font-bold text-sm transition-colors"
            >
              <Phone className="w-4 h-4" /> Call (225) 892-7777 to Confirm
            </a>
          </div>
        ) : config?.configured ? (
          <div data-testid="deposit-paypal-buttons">
            <div data-testid="deposit-checkout-amount" className="font-display text-4xl font-bold text-[#1E252B] mb-1">${amount}</div>
            <p className="text-xs text-[#736B63] mb-6">One-time booking deposit &bull; Applied to your final invoice</p>
            <PayPalScriptProvider options={{ clientId: config.client_id, currency: config.currency || "USD", intent: "capture" }}>
              <PayPalButtons
                style={{ layout: "vertical", color: "gold", shape: "rect", label: "paypal" }}
                data-testid="paypal-button"
                createOrder={async () => {
                  const r = await axios.post(`${API}/paypal/orders`, {});
                  return r.data.id;
                }}
                onApprove={async (data) => {
                  try {
                    const r = await axios.post(`${API}/paypal/orders/${data.orderID}/capture`);
                    if (r.data.status === "COMPLETED") {
                      setPaid(true);
                      toast.success("Deposit received — you're locked in for priority scheduling.");
                    } else {
                      toast.error("Payment did not complete. Please try again.");
                    }
                  } catch (e) {
                    toast.error("Could not confirm payment. Please call (225) 892-7777.");
                  }
                }}
                onError={() => toast.error("PayPal checkout hit a snag. Please try again or call (225) 892-7777.")}
              />
            </PayPalScriptProvider>
          </div>
        ) : (
          <div data-testid="deposit-pending-notice">
            <div data-testid="deposit-pending-amount" className="font-display text-4xl font-bold text-[#1E252B] mb-1">${amount}</div>
            <p className="text-xs text-[#736B63] mb-4">One-time booking deposit &bull; Applied to your final invoice</p>
            <div className="bg-[#FFF3EA] border border-[#D4691E]/30 rounded-lg p-4 text-sm text-[#A94E10] font-semibold">
              Online deposit checkout is being connected. To lock in priority scheduling today, call{" "}
              <a href="tel:+12258927777" className="underline" data-testid="deposit-pending-call-link">
                (225) 892-7777
              </a>{" "}
              or submit a quote request below.
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
